import { ReactNode, useEffect } from 'react';
import { IntercomProvider as IComProvider, useIntercom } from 'react-use-intercom';
import { useUserContext } from './UserProvider';
import { useFirmContext } from './FirmProvider';
import { INTERCOM_APP_ID } from '../config';
import { useLocation } from 'react-router-dom';
import { useIntercomHash } from '../queries/use-intercom-hash';
import { useUserFirmRoleContext } from './UserFirmRoleProvider';
import { usePaymentMethods } from '../queries/billing/use-payment-methods';

const INTERCOM_API_BASE = 'https://api-iam.intercom.io';

type IntercomProviderProps = {
    children: ReactNode | ReactNode[];
};

function IntercomProvider({ children }: IntercomProviderProps): JSX.Element {
    return (
        <IComProvider appId={INTERCOM_APP_ID || ''} shouldInitialize={Boolean(INTERCOM_APP_ID)} apiBase={INTERCOM_API_BASE}>
            <IntercomInitializer>{children}</IntercomInitializer>
        </IComProvider>
    );
}

function IntercomInitializer({ children }: IntercomProviderProps): JSX.Element {
    const intercom = useIntercom();
    const user = useUserContext();
    const firm = useFirmContext();
    const location = useLocation();
    const role = useUserFirmRoleContext();
    const hashQuery = useIntercomHash(user.id);

    useEffect(() => {
        if (user && hashQuery.data?.hash) {
            const name = `${user.firstName} ${user.lastName}`;
            intercom.boot({
                name,
                userId: user.id,
                email: user.email,
                userHash: hashQuery.data.hash,
                createdAt: new Date(user.createdAt).getTime() / 1000, // seconds,
                customAttributes: {
                    'Current role': role,
                },
                companies: [
                    {
                        name: firm.name,
                        companyId: firm.id,
                        createdAt: new Date(firm.createdAt).getTime() / 1000,
                        plan: firm.subscription?.tier,
                        customAttributes: {
                            'Firm short code': firm.shortId,
                            'Trial Start': firm.subscriptionVersion !== 1 ? firm.subscription?.trialStart : null,
                            'Trial End': firm.subscription?.trialEnd,
                            'Has Payment Method': firm.subscriptionVersion !== 1 ? firm.subscription?.hasPaymentMethod : null,
                            'Subscription Status': firm.subscriptionVersion !== 1 ? firm.subscription?.status : null,
                        },
                    },
                ],
            });
        }
    }, [intercom, firm, user, role, hashQuery.data?.hash]);

    useEffect(() => {
        intercom.update();
    }, [intercom, location.pathname]);

    return <>{children}</>;
}

export default IntercomProvider;
